import { FUND_STORAGE_KEYS } from "@core/constants";
import {
  columns,
  FUND_COLUMN_DEFAULT_VISIBILITY,
} from "@funds/components/column";
import { FILTER_CONFIG, FilterConfig } from "@funds/config";
import { VisibilityState } from "@tanstack/react-table";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import { mergeColumnOrder, mergeColumnVisibility } from "@/utils/column-order";

const DEFAULT_COLUMN_ORDER = columns.map((c) => c.id as string);

export function useFundsTableConfig() {
  const [storedColumnOrder, setColumnOrder] = useLocalStorage<string[]>(
    FUND_STORAGE_KEYS.ColumnOrder,
    DEFAULT_COLUMN_ORDER
  );

  const [storedColumnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      FUND_STORAGE_KEYS.ColumnVisibility,
      FUND_COLUMN_DEFAULT_VISIBILITY
    );

  // Reconciled on read, so added, renamed or removed columns need no storage version bump
  const columnOrder = useMemo(
    () => mergeColumnOrder(storedColumnOrder, DEFAULT_COLUMN_ORDER),
    [storedColumnOrder]
  );

  const columnVisibility = useMemo(
    () =>
      mergeColumnVisibility(
        storedColumnVisibility,
        FUND_COLUMN_DEFAULT_VISIBILITY,
        DEFAULT_COLUMN_ORDER
      ),
    [storedColumnVisibility]
  );

  const [filterConfig, setFilterConfig] = useLocalStorage<
    Partial<FilterConfig>[]
  >(
    FUND_STORAGE_KEYS.FilterVisibility,
    FILTER_CONFIG.map((fc) => ({ id: fc.id, visible: fc.visible }))
  );

  const [filtersOrder, setFiltersOrder] = useLocalStorage<string[]>(
    FUND_STORAGE_KEYS.FilterOrder,
    []
  );

  const sortedFiltersConfig = useMemo(
    () =>
      FILTER_CONFIG.map((f) => {
        const overrides = filterConfig.find((fc) => fc.id === f.id);
        return { ...f, ...overrides };
      }).toSorted(
        (a, b) => filtersOrder.indexOf(a.id) - filtersOrder.indexOf(b.id)
      ) as FilterConfig[],
    [filterConfig, filtersOrder]
  );

  return useMemo(
    () => ({
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
      columnOrder,
      onColumnOrderChange: setColumnOrder,
      filterConfig: sortedFiltersConfig,
      setFilterConfig,
      filtersOrder,
      onFiltersOrderChange: setFiltersOrder,
    }),
    [
      columnVisibility,
      setColumnVisibility,
      setColumnOrder,
      columnOrder,
      sortedFiltersConfig,
      setFilterConfig,
      filtersOrder,
      setFiltersOrder,
    ]
  );
}
